import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import stringify from 'fast-json-stable-stringify';
import ProfileListFilters from '@fuww/library/src/ProfileListFilters';
import ListPage from '@fuww/library/src/Layouts/GridListPage';
import Hero from '../Hero';
import Head from '../Head';
import Layout from '../Layout';
import LinkAndAnchor from '../LinkAndAnchor';
import messages from '../../lib/messages.json';
import HomeLink from '../HomeLink';
import debouncedReplaceRoute from '../../lib/debouncedReplaceRoute';
import replaceRoute from '../../lib/replaceRoute';
import CountrySelect from '../CountrySelect';

const DirectoryLayout = ({ children, imageUrls, alt }) => (
  <Layout
    header={(
      <Hero
        alt={alt}
        imageUrls={imageUrls}
      />
      )}
  >
    {children}
  </Layout>
);

DirectoryLayout.propTypes = {
  children: PropTypes.node.isRequired,
  imageUrls: PropTypes.shape({
    mobile: PropTypes.arrayOf(PropTypes.string),
    desktop: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  alt: PropTypes.string.isRequired,
};

const DirectoryPage = ({
  route,
  pageType,
  ComponentWithQuery,
  query,
  filters,
  variables,
  routeParams,
  title,
}) => {
  const intl = useIntl();
  const { query: queryParameters } = useRouter();
  const {
    search: searchParameter,
    country: countryParameter,
    section,
  } = queryParameters;

  const [search, setSearch] = useState(searchParameter || '');

  const stringifiedRouteParameters = stringify(routeParams);

  const stringifiedQueryParameters = stringify(queryParameters);

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route={route}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={title}
        description={
          intl.formatMessage(messages[`${pageType}-directory.description`])
        }
      />
      <ListPage
        breadcrumbs={breadcrumbs}
        title={title}
        filters={(
          <ProfileListFilters
            extraFilters={filters}
            searchId={`${pageType}-directory-search`}
            searchValue={search}
            searchLabel={intl.formatMessage(
              messages.searchLabel,
              { pageTitle: title },
            )}
            searchOnChange={({ target: { value } }) => {
              setSearch(value);
              const { search: _search, ...rest } = queryParameters;
              debouncedReplaceRoute(route, {
                ...rest,
                ...(value ? { search: value } : {}),
                ...routeParams,
              });
            }}
            searchOnClear={() => {
              setSearch('');
              const { search: _search, ...rest } = queryParameters;
              replaceRoute(route, {
                ...rest,
                ...routeParams,
              });
            }}
            countryId={`${pageType}-directory-select`}
            countryValue={countryParameter || ''}
            countryOnChange={useCallback((event) => {
              const country = event.target.value;
              const {
                country: _country,
                ...rest
              } = JSON.parse(stringifiedQueryParameters);
              replaceRoute(route, {
                ...rest,
                ...(country ? { country } : {}),
                ...JSON.parse(stringifiedRouteParameters),
              });
            }, [stringifiedRouteParameters, stringifiedQueryParameters, route])}
            allCountriesLabel={intl.formatMessage(
              messages['brands-directory.all-countries.label'],
            )}
            premiumChecked={section !== 'all'}
            premiumOnChange={({ target: { checked } }) => {
              const { section: _section, ...rest } = queryParameters;
              replaceRoute(route, {
                ...rest,
                ...(checked ? {} : { section: 'all' }),
                ...routeParams,
              });
            }}
            CountryField={CountrySelect}
          />
        )}
        list={(
          <ComponentWithQuery
            query={query}
            variables={variables}
            errorMessage={`Error loading ${pageType}`}
          />
        )}
      />
    </>
  );
};

DirectoryPage.propTypes = {
  route: PropTypes.string.isRequired,
  pageType: PropTypes.string.isRequired,
  ComponentWithQuery: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  filters: PropTypes.node,
  variables: PropTypes.shape({}).isRequired,
  routeParams: PropTypes.shape({}),
  title: PropTypes.string.isRequired,
};

DirectoryPage.defaultProps = {
  filters: null,
  routeParams: null,
};

export {
  DirectoryPage,
  DirectoryLayout,
};
